/**
 * Analytics User Data Store
 *
 * Caches authenticated user details so every CAPI event and Advanced
 * Matching call can include them for higher Event Match Quality scores.
 *
 * Supported Meta matching parameters:
 *   external_id, em (email), ph (phone), fn (first name), ln (last name),
 *   ct (city), st (state), zp (zip), dob, fb_login_id
 *
 * Call `setAnalyticsUser` when the auth state changes and
 * `clearAnalyticsUser` on sign-out.
 */

interface AnalyticsUserData {
  external_id?: string;
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  ct?: string;
  st?: string;
  zp?: string;
  dob?: string;
  fb_login_id?: string;
}

let cachedUser: AnalyticsUserData = {};

/** Update the cached user data used for event enrichment. */
export function setAnalyticsUser(data: {
  userId?: string;
  email?: string;
  phone?: string;
  fullName?: string;
  address?: string;
  dob?: string;
  fbLoginId?: string;
}): void {
  cachedUser = {};
  if (data.userId) cachedUser.external_id = data.userId;
  if (data.email) cachedUser.em = data.email;
  if (data.phone) cachedUser.ph = data.phone;
  if (data.fbLoginId) cachedUser.fb_login_id = data.fbLoginId;
  if (data.dob) cachedUser.dob = data.dob;

  // Split full_name into first / last name
  if (data.fullName) {
    const parts = data.fullName.trim().split(/\s+/);
    if (parts.length >= 1) cachedUser.fn = parts[0];
    if (parts.length >= 2) cachedUser.ln = parts.slice(1).join(' ');
  }

  // Try to parse address for city / state / zip
  if (data.address) {
    const parsed = parseAddress(data.address);
    if (parsed.zip) cachedUser.zp = parsed.zip;
    if (parsed.city) cachedUser.ct = parsed.city;
    if (parsed.state) cachedUser.st = parsed.state;
  }
}

/** Clear cached user data (e.g. on sign-out). */
export function clearAnalyticsUser(): void {
  cachedUser = {};
}

/**
 * Return a shallow copy of the current user data for merging into
 * CAPI event payloads.  Empty fields are omitted.
 */
export function getAnalyticsUserData(): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(cachedUser)) {
    if (value) out[key] = value;
  }
  return out;
}

/**
 * Return user data formatted for Meta Pixel Advanced Matching.
 * Keys match Meta's expected parameter names.
 */
export function getAdvancedMatchingParams(): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(cachedUser)) {
    if (value) params[key] = value;
  }
  return Object.keys(params).length > 0 ? params : undefined;
}

// ── Address parsing helper ──────────────────────────────────────────────────

interface ParsedAddress {
  city?: string;
  state?: string;
  zip?: string;
}

/**
 * Best-effort extraction of city, state, zip from a free-form address string.
 * Handles common patterns like "123 Main St, City, ST 12345" or "City, State".
 */
function parseAddress(address: string): ParsedAddress {
  const result: ParsedAddress = {};

  // Extract zip code (5-digit or 5+4 US zip, or 4-6 digit European postal code)
  const zipMatch = address.match(/\b(\d{4,6}(?:-\d{4})?)\b/);
  if (zipMatch) result.zip = zipMatch[1];

  // Try "City, ST ZIP" or "City, State" pattern
  const parts = address.split(',').map((p) => p.trim());
  if (parts.length >= 2) {
    // Last part may contain state + zip
    const lastPart = parts[parts.length - 1];
    const stateZipMatch = lastPart.match(/^([A-Za-z\s]+?)(?:\s+\d{4,6}(?:-\d{4})?)?$/);
    if (stateZipMatch) {
      const stateCandidate = stateZipMatch[1].trim();
      if (stateCandidate.length <= 20) {
        result.state = stateCandidate;
      }
    }
    // Second-to-last part is typically the city
    const cityPart = parts[parts.length - 2].trim();
    // Strip leading street numbers/names heuristically — take last segment if it has a number prefix
    if (cityPart && !cityPart.match(/^\d/)) {
      result.city = cityPart;
    }
  }

  return result;
}
